(function () {
  const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
  const TERMS_VERSION = 'ankauf_terms_v2';
  const REQUIRED_TERMS = ['ankauf_flow_v2'];
  const PAUSE_STATUS_MESSAGE = 'Der Ankauf ist aktuell pausiert.';
  const PAUSED_CONTROL_SELECTOR = 'input, select, textarea, button';
  const CART_PROPERTY_BLOCKLIST = new Set([
    'downloadToken',
    '_downloadToken',
    'pdfUrl',
    '_pdfUrl',
    'adminUrl',
    '_adminUrl',
    'rawCards',
    'fileContent',
    'lines',
  ]);

  const money = (cents, currency = 'EUR') => {
    const value = Number.isFinite(Number(cents)) ? Math.round(Number(cents)) : 0;
    return `${Math.floor(value / 100)},${String(Math.abs(value % 100)).padStart(2, '0')} ${currency}`;
  };

  const moneyEuro = (cents) => {
    const value = Number.isFinite(Number(cents)) ? Math.round(Number(cents)) : 0;
    return `${Math.floor(value / 100)},${String(Math.abs(value % 100)).padStart(2, '0')} €`;
  };

  const positiveInteger = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.floor(number)) : 0;
  };

  const isFiniteNonNegative = (value) => value !== null
    && value !== undefined
    && value !== ''
    && Number.isFinite(Number(value))
    && Number(value) >= 0;

  const normalizeApiBasePath = (value) => String(value || '').trim().replace(/\/+$/, '');

  const uploadFingerprint = (file, counts) => JSON.stringify({
    fileName: file?.name || '',
    fileType: file?.type || '',
    fileSize: file?.size ?? file?.sizeBytes ?? null,
    lastModified: file?.lastModified ?? null,
    counts,
  });

  const isDraftFresh = (draft, now = Date.now()) => Boolean(
    draft && Number.isFinite(draft.savedAt) && now - draft.savedAt < DRAFT_TTL_MS
  );

  const isAcceptedUploadFile = (file) => {
    if (!file || typeof file.name !== 'string') return false;
    const name = file.name.trim().toLowerCase();
    return name.endsWith('.csv') || name.endsWith('.json');
  };

  const fileType = (file) => String(file?.type || '').toLowerCase().includes('json') || String(file?.name || '').toLowerCase().endsWith('.json')
    ? 'json'
    : 'csv';

  const resolveHoloReverseCount = (bulk = {}, counts = {}) => {
    if (isFiniteNonNegative(bulk.holoReverseCount)) return positiveInteger(bulk.holoReverseCount);
    if (isFiniteNonNegative(bulk.holoCount) || isFiniteNonNegative(bulk.reverseHoloCount)) {
      return positiveInteger(bulk.holoCount) + positiveInteger(bulk.reverseHoloCount);
    }
    return positiveInteger(counts.holoReverseCount);
  };

  const sanitizeCartProperties = (properties = {}) => {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return {};
    return Object.entries(properties).reduce((safe, [key, value]) => {
      const normalizedKey = String(key || '').trim();
      if (!normalizedKey || CART_PROPERTY_BLOCKLIST.has(normalizedKey) || value == null || typeof value === 'object') return safe;
      safe[normalizedKey] = String(value);
      return safe;
    }, {});
  };

  const normalizeUploadResponse = (response, counts = {}) => {
    const file = response?.file || {};
    const bulk = response?.bulk || {};
    return {
      buybackSubmissionId: String(response?.buybackSubmissionId || '').trim(),
      buybackCartLinkToken: String(response?.buybackCartLinkToken || '').trim(),
      file: response?.file === null ? null : {
        name: String(file.name || file.originalName || '').trim(),
        type: fileType({ ...file, name: file.name || file.originalName, type: file.type || file.contentType }),
        sizeBytes: positiveInteger(file.sizeBytes ?? file.byteSize),
        sha256: String(file.sha256 || '').trim(),
      },
      bulk: {
        commonUncommonCount: positiveInteger(bulk.commonUncommonCount ?? counts.commonUncommonCount),
        holoReverseCount: resolveHoloReverseCount(bulk, counts),
        doubleRareCount: positiveInteger(bulk.doubleRareCount ?? counts.doubleRareCount),
        totalCents: positiveInteger(bulk.totalCents),
      },
    };
  };

  const isValidUploadResponse = (response) => {
    const file = response?.file || {};
    const rawType = String(file.type || file.contentType || '').trim().toLowerCase();
    const normalizedType = fileType({ ...file, name: file.name || file.originalName, type: rawType });
    const fileName = String(file.name || file.originalName || '').trim();
    const sizeBytes = file.sizeBytes ?? file.byteSize;
    const supportedType = rawType === 'csv'
      || rawType === 'json'
      || rawType.includes('csv')
      || rawType.includes('json');
    const bulkCounts = ['commonUncommonCount', 'holoReverseCount', 'doubleRareCount']
      .map((key) => response?.bulk?.[key]);
    const validBulkOnly = response?.file === null
      && bulkCounts.every((value) => isFiniteNonNegative(value) && Number.isSafeInteger(Number(value)))
      && bulkCounts.some((value) => Number(value) > 0);
    const validFile = fileName && rawType && supportedType
      && ['csv', 'json'].includes(normalizedType) && isFiniteNonNegative(sizeBytes);
    return Boolean(
      String(response?.buybackSubmissionId || '').trim()
      && String(response?.buybackCartLinkToken || '').trim()
      && (validBulkOnly || validFile)
      && isFiniteNonNegative(response?.bulk?.totalCents)
    );
  };

  const createCartPayload = (variantId, upload) => {
    const normalized = normalizeUploadResponse(upload);
    const currency = upload?.currency || 'EUR';
    return {
      id: variantId,
      quantity: 1,
      properties: sanitizeCartProperties({
        _buybackSubmissionId: normalized.buybackSubmissionId,
        _buybackCartLinkToken: normalized.buybackCartLinkToken,
        _uploadFileName: normalized.file?.name,
        _uploadFileType: normalized.file?.type,
        'Common/Uncommon/Rare Anzahl': normalized.bulk.commonUncommonCount,
        'Holo/Reverse Holo Anzahl': normalized.bulk.holoReverseCount,
        'Double Rare Anzahl': normalized.bulk.doubleRareCount,
        'Vorläufige Bulk-Auszahlung': money(normalized.bulk.totalCents, currency),
      }),
    };
  };

  const shouldRejectProductionMock = (config = {}) => config.adapterMode === 'mock';

  const shopifyRouteRoot = () => {
    const root = String(window.Shopify?.routes?.root || '/').trim() || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const buildShopifyCartUrl = (path) => {
    const normalizedPath = String(path || '').replace(/^\/+/, '');
    return normalizedPath.startsWith('.')
      ? `${shopifyRouteRoot()}cart${normalizedPath}`
      : `${shopifyRouteRoot()}cart/${normalizedPath}`;
  };

  const isLocalCartTestContext = () => {
    const hostname = String(window.location?.hostname || '').toLowerCase();
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
  };

  const canCreateCartLines = (config = {}) => (
    config.adapterMode !== 'mock' || window.Shopify?.designMode === true || isLocalCartTestContext()
  );

  const resolveTermsVersion = (config = {}) => String(config.termsVersion || TERMS_VERSION).trim() || TERMS_VERSION;

  const resolveRequiredTerms = (config = {}) => {
    const terms = Array.isArray(config.requiredTerms)
      ? config.requiredTerms.map((term) => String(term || '').trim()).filter(Boolean)
      : [];
    return terms.length ? terms : REQUIRED_TERMS.slice();
  };

  const parseRetryAfterSeconds = (value, nowMs = Date.now()) => {
    if (typeof value !== 'string' || !value.trim()) return null;
    const text = value.trim();
    let seconds;
    if (/^\d+$/.test(text)) seconds = Number(text);
    else {
      if (!/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun), \d{2} (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4} \d{2}:\d{2}:\d{2} GMT$/.test(text)) return null;
      const dateMs = Date.parse(text);
      if (!Number.isFinite(dateMs) || !Number.isFinite(nowMs)) return null;
      if (new Date(dateMs).toUTCString() !== text) return null;
      seconds = Math.ceil((dateMs - nowMs) / 1000);
    }
    return Number.isSafeInteger(seconds) && seconds > 0 && seconds <= 86400 ? seconds : null;
  };

  const uploadWaitSuffix = (retryAfter, nowMs) => {
    const seconds = parseRetryAfterSeconds(retryAfter, nowMs);
    if (seconds === null) return 'Bitte versuche es später erneut.';
    if (seconds < 60) return `Bitte versuche es in ${seconds} ${seconds === 1 ? 'Sekunde' : 'Sekunden'} erneut.`;
    const minutes = Math.ceil(seconds / 60);
    return `Bitte versuche es in ${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'} erneut.`;
  };

  const UPLOAD_VALIDATION_MESSAGES = new Map([
    ['Upload must not be empty.', 'Die Datei ist leer. Bitte exportiere deine Kartenliste erneut.'],
    ['Upload is too large. Maximum size is 10 MB.', 'Die Datei ist zu groß. Maximal erlaubt sind 10 MB.'],
    ['Unsupported file type. Only CSV and JSON uploads are accepted.', 'Bitte lade den originalen CSV- oder JSON-Export hoch.'],
    ['Binary files are not accepted.', 'Die Datei enthält kein unterstütztes Textformat. Bitte lade den originalen CSV-/JSON-Export hoch.'],
    ['Upload must be valid UTF-8 text.', 'Die Datei ist nicht als UTF-8 gespeichert. Bitte exportiere sie erneut als UTF-8.'],
    ['CSV exceeds the maximum of 20,000 lines.', 'Die CSV-Datei enthält mehr als 20.000 Zeilen. Bitte teile den Export auf.'],
    ['JSON upload must contain valid JSON.', 'Die JSON-Datei ist nicht gültig. Bitte exportiere deine Kartenliste erneut.'],
    ['JSON upload must contain an object or array.', 'Die JSON-Datei muss ein Objekt oder eine Liste enthalten. Bitte verwende den originalen Export.'],
    ['JSON exceeds the maximum depth of 20.', 'Die JSON-Datei ist zu stark verschachtelt. Bitte verwende den originalen Export.'],
    ['Upload does not contain enough text data.', 'Die Datei enthält kein unterstütztes Textformat. Bitte lade den originalen CSV-/JSON-Export hoch.'],
  ]);

  const getUploadFailureMessage = (input = {}, nowMs = Date.now()) => {
    const { status, code, message, retryAfter } = input;
    if (status === 429 || code === 'rate_limited') {
      return `Zu viele Uploadversuche in kurzer Zeit. ${uploadWaitSuffix(retryAfter, nowMs)}`;
    }
    if (status === 503 || code === 'rate_limit_unavailable') {
      return `Der Uploaddienst ist vorübergehend nicht verfügbar. ${uploadWaitSuffix(retryAfter, nowMs)}`;
    }
    if (code === 'missing_file') return 'Bitte eine CSV- oder JSON-Datei auswählen.';
    if (code === 'bad_multipart') return 'Die Datei konnte nicht übertragen werden. Bitte wähle sie erneut aus.';
    if (status === 401 || status === 403) {
      return 'Die Verbindung zum Uploaddienst konnte nicht bestätigt werden. Bitte lade die Seite neu. Falls der Fehler bleibt, kontaktiere uns.';
    }
    if (status === 413) return 'Die Datei ist zu groß. Maximal erlaubt sind 10 MB.';
    if (code === 'upload_invalid') {
      return UPLOAD_VALIDATION_MESSAGES.get(message)
        || 'Die Datei oder die Mengenangaben konnten nicht geprüft werden. Bitte prüfe den CSV-/JSON-Export und deine Eingaben.';
    }
    if (status >= 500 || code === 'upload_failed') {
      return 'Die Datei konnte gerade nicht gespeichert werden. Bitte versuche es später erneut.';
    }
    return 'Der Upload ist fehlgeschlagen. Bitte wähle die CSV-/JSON-Datei erneut aus und versuche es nochmals.';
  };

  const readUploadFailureMessage = async (response) => {
    let body;
    try { body = await response.json(); } catch (_error) {}
    return getUploadFailureMessage({
      status: response.status,
      code: typeof body?.error?.code === 'string' ? body.error.code : '',
      message: typeof body?.error?.message === 'string' ? body.error.message : '',
      retryAfter: response.headers?.get?.('retry-after') ?? null,
    });
  };

  class AnkaufMode {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-ankauf-config]').textContent);
      this.state = { upload: null, uploadFile: null, uploadFingerprint: '' };
      this.isSubmitting = false;
      this.abortController = null;
      this.bind();
      this.restoreDraft();
      this.render();
      this.serviceFeeLabelPromise = this.resolveServiceFeeLabel();
      this.applyPausedState();
    }

    isPaused() {
      return this.config.paused === true;
    }

    pauseMessage() {
      return String(this.config.pauseMessage || PAUSE_STATUS_MESSAGE).trim() || PAUSE_STATUS_MESSAGE;
    }

    abortIfPaused() {
      if (!this.isPaused()) return false;
      this.abortController?.abort();
      this.setStatus(this.pauseMessage(), true);
      this.applyPausedState();
      return true;
    }

    applyPausedState() {
      const paused = this.isPaused();
      const locked = paused || this.isSubmitting;
      this.root.classList?.toggle('is-paused', paused);
      Array.from(this.root.querySelectorAll(PAUSED_CONTROL_SELECTOR)).forEach((control) => {
        if (locked) {
          if (!control.dataset?.ankaufOriginalDisabled) control.dataset.ankaufOriginalDisabled = control.disabled ? 'true' : 'false';
          control.disabled = true;
          control.setAttribute?.('aria-disabled', 'true');
        } else if (control.dataset?.ankaufOriginalDisabled) {
          control.disabled = control.dataset.ankaufOriginalDisabled === 'true';
          delete control.dataset.ankaufOriginalDisabled;
          control.removeAttribute?.('aria-disabled');
        }
      });
      if (this.submitButton) {
        this.submitButton.disabled = paused || this.isSubmitting;
        this.submitButton.setAttribute?.('aria-busy', String(this.isSubmitting));
      }
    }

    bind() {
      this.bulkInputs = Array.from(this.root.querySelectorAll('[data-ankauf-bulk-input]'));
      this.uploadInput = this.root.querySelector('[data-ankauf-upload]');
      this.uploadName = this.root.querySelector('[data-ankauf-upload-name]');
      this.totalCount = this.root.querySelector('[data-ankauf-total-count]');
      this.totalPrice = this.root.querySelector('[data-ankauf-total-price]');
      this.serviceFee = this.root.querySelector('[data-ankauf-service-fee]');
      this.status = this.root.querySelector('[data-ankauf-status]');
      this.submitForm = this.root.querySelector('[data-ankauf-submit-form]');
      this.submitButton = this.root.querySelector('[data-ankauf-submit]');

      this.bulkInputs.forEach((input) => input.addEventListener('input', () => {
        if (this.abortIfPaused()) return;
        this.invalidateUpload();
        this.persistDraft();
        this.renderBulkSummary();
      }));
      this.uploadInput?.addEventListener('change', () => {
        if (this.abortIfPaused()) return;
        this.invalidateUpload();
        this.renderUploadName();
      });
      this.submitForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        if (this.abortIfPaused()) return;
        this.submit();
      });
    }

    destroy() {
      this.abortController?.abort();
      window.clearTimeout?.(this.statusTimer);
      if (this.root.__ankaufMode === this) delete this.root.__ankaufMode;
    }

    readCounts() {
      const get = (name) => positiveInteger(this.root.querySelector(`[data-ankauf-bulk-input="${name}"]`)?.value);
      return {
        commonUncommonCount: get('commonUncommon'),
        holoReverseCount: get('holoReverse'),
        doubleRareCount: get('doubleRare'),
      };
    }

    fallbackBulkTotalCents(counts = this.readCounts()) {
      return counts.commonUncommonCount * positiveInteger(this.config.commonUncommonUnitCents)
        + counts.holoReverseCount * positiveInteger(this.config.holoReverseUnitCents)
        + counts.doubleRareCount * positiveInteger(this.config.doubleRareUnitCents);
    }

    renderBulkSummary() {
      const counts = this.readCounts();
      const totalCount = counts.commonUncommonCount + counts.holoReverseCount + counts.doubleRareCount;
      const totalCents = this.state.upload?.bulk?.totalCents ?? this.fallbackBulkTotalCents(counts);
      if (this.totalCount) this.totalCount.textContent = String(totalCount);
      if (this.totalPrice) this.totalPrice.textContent = money(totalCents);
      this.root.querySelectorAll('[data-ankauf-bulk-price]').forEach((node) => {
        const units = {
          commonUncommon: this.config.commonUncommonUnitCents,
          holoReverse: this.config.holoReverseUnitCents,
          doubleRare: this.config.doubleRareUnitCents,
        };
        const unit = positiveInteger(units[node.dataset.ankaufBulkPrice]);
        node.textContent = unit ? `${money(unit)} / Karte` : '';
      });
    }

    renderUploadName() {
      const file = this.uploadInput?.files?.[0];
      if (this.uploadName) this.uploadName.textContent = file?.name || 'Noch keine Datei ausgewählt';
    }

    render() {
      this.renderBulkSummary();
      this.renderUploadName();
      this.applyPausedState();
    }

    async resolveServiceFeeLabel() {
      if (!this.serviceFee) return;
      const configuredLabel = String(this.config.serviceFeeLabel || '').trim();
      if (configuredLabel) {
        this.serviceFee.textContent = configuredLabel;
        return;
      }
      if (!String(this.serviceFee.textContent || '').trim()) return;
      const variantId = String(this.config.serviceVariantId || '').trim();
      if (!variantId || !/^\d+$/.test(variantId)) return;
      try {
        const response = await fetch(`${shopifyRouteRoot()}variants/${variantId}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const variant = await response.json();
        if (isFiniteNonNegative(variant?.price)) this.serviceFee.textContent = moneyEuro(variant.price);
      } catch (_error) {}
    }

    invalidateUpload() {
      this.state.upload = null;
      this.state.uploadFile = null;
      this.state.uploadFingerprint = '';
    }

    selectedTerms() {
      return Array.from(this.root.querySelectorAll('[data-ankauf-term]:checked')).map((input) => input.value);
    }

    validateBeforeUpload() {
      const fileMessage = this.validateSubmissionContents();
      if (fileMessage) return fileMessage;
      const terms = this.selectedTerms();
      if (!resolveRequiredTerms(this.config).every((term) => terms.includes(term))) return 'Bitte die Pflichtbestätigung akzeptieren.';
      return '';
    }

    validateSubmissionContents() {
      const file = this.uploadInput?.files?.[0];
      if (!file) {
        return Object.values(this.readCounts()).some((count) => count > 0)
          ? '' : 'Bitte mindestens eine Bulk-Karte eintragen oder eine MonPrice-Datei auswählen.';
      }
      if (!isAcceptedUploadFile(file)) return 'Bitte eine CSV- oder JSON-Datei auswählen.';
      const maxBytes = positiveInteger(this.config.maxUploadMb || 10) * 1024 * 1024;
      const size = positiveInteger(file.size ?? file.sizeBytes);
      if (size > maxBytes) return `Die Datei ist zu groß. Maximal erlaubt sind ${this.config.maxUploadMb || 10} MB.`;
      return '';
    }

    async submit() {
      if (this.abortIfPaused()) return;
      if (this.isSubmitting) return;
      if (!this.config.serviceVariantId) return this.setStatus('Service-Produkt Variant ID fehlt in den Section Settings.', true);
      const validationMessage = this.validateBeforeUpload();
      if (validationMessage) return this.setStatus(validationMessage, true);

      this.setSubmitPending(true);
      try {
        this.setStatus('Ankaufangaben werden gespeichert.');
        const upload = await this.uploadSubmission();
        if (this.abortIfPaused()) return;
        await this.addServiceProduct(upload);
        this.state.upload = null;
        this.clearCounts();
        this.persistDraft();
        this.render();
        this.setStatus('Ankaufangaben wurden gespeichert und in den Warenkorb gelegt.');
      } catch (error) {
        this.setStatus(error.message || 'Upload fehlgeschlagen.', true);
      } finally {
        this.setSubmitPending(false);
      }
    }

    async uploadSubmission() {
      if (this.abortIfPaused()) throw new Error(this.pauseMessage());
      const file = this.uploadInput?.files?.[0];
      const counts = this.readCounts();
      const validationMessage = this.validateSubmissionContents();
      if (validationMessage) throw new Error(validationMessage);
      const fingerprint = uploadFingerprint(file, counts);
      if (this.state.upload && this.state.uploadFile === file && this.state.uploadFingerprint === fingerprint) {
        return this.state.upload;
      }
      this.invalidateUpload();
      if (this.config.adapterMode === 'mock') {
        const totalCents = this.fallbackBulkTotalCents(counts);
        const mock = normalizeUploadResponse({
          ok: true,
          buybackSubmissionId: 'MOCK-BUYBACK',
          buybackCartLinkToken: 'mock-token',
          file: file ? { name: file.name, type: fileType(file), sizeBytes: positiveInteger(file.size ?? file.sizeBytes) } : null,
          bulk: { ...counts, totalCents },
        }, counts);
        this.state.upload = mock;
        this.state.uploadFile = file;
        this.state.uploadFingerprint = fingerprint;
        return mock;
      }

      const form = new FormData();
      if (file) form.append('file', file);
      form.append('commonUncommonCount', String(counts.commonUncommonCount));
      form.append('holoReverseCount', String(counts.holoReverseCount));
      form.append('doubleRareCount', String(counts.doubleRareCount));
      this.abortController?.abort();
      this.abortController = new AbortController();
      let response;
      try {
        response = await fetch(`${normalizeApiBasePath(this.config.apiBasePath)}/uploads`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: form,
          signal: this.abortController.signal,
        });
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        throw new Error('Die Verbindung wurde unterbrochen. Bitte prüfe deine Internetverbindung und versuche es erneut.');
      }
      if (!response.ok) throw new Error(await readUploadFailureMessage(response));
      let body;
      try { body = await response.json(); }
      catch (_error) { throw new Error('Upload-Antwort ist unvollständig.'); }
      if (!isValidUploadResponse(body)) throw new Error('Upload-Antwort ist unvollständig.');
      const normalized = normalizeUploadResponse(body, counts);
      this.state.upload = normalized;
      this.state.uploadFile = file;
      this.state.uploadFingerprint = fingerprint;
      return normalized;
    }

    setSubmitPending(pending) {
      this.isSubmitting = Boolean(pending);
      this.applyPausedState();
    }

    async addServiceProduct(upload) {
      if (this.abortIfPaused()) throw new Error(this.pauseMessage());
      if (!canCreateCartLines(this.config)) throw new Error('Testmodus kann außerhalb des Theme Editors keinen echten Warenkorb anlegen.');
      if (!isValidUploadResponse(upload)) throw new Error('Upload-Antwort ist unvollständig.');
      const existingLines = await this.findExistingServiceProductLines();
      const response = await fetch(buildShopifyCartUrl('add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(createCartPayload(this.config.serviceVariantId, upload)),
      });
      if (!response.ok) throw new Error('Service-Produkt konnte nicht in den Warenkorb gelegt werden.');
      await this.removeExistingServiceProductLines(existingLines);
    }

    async findExistingServiceProductLines() {
      const variantId = String(this.config.serviceVariantId || '');
      if (!variantId) return [];
      const response = await fetch(buildShopifyCartUrl('.js'), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Warenkorb konnte nicht geprüft werden.');
      const cart = await response.json();
      return Array.isArray(cart?.items)
        ? cart.items.filter((item) => String(item.variant_id || item.id || '') === variantId)
        : [];
    }

    async removeExistingServiceProductLines(existingLines = null) {
      const lines = existingLines || await this.findExistingServiceProductLines();
      for (const item of lines) {
        const lineId = item.key || item.id || item.variant_id;
        if (!lineId) continue;
        const removeResponse = await fetch(buildShopifyCartUrl('change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineId, quantity: 0 }),
        });
        if (!removeResponse.ok) throw new Error('Vorhandenes Service-Produkt konnte nicht entfernt werden.');
      }
    }

    clearCounts() {
      this.invalidateUpload();
      ['commonUncommon', 'holoReverse', 'doubleRare'].forEach((name) => {
        const input = this.root.querySelector(`[data-ankauf-bulk-input="${name}"]`);
        if (input) input.value = '0';
      });
      if (this.uploadInput) this.uploadInput.value = '';
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList?.toggle('q-ankauf__error', isError);
    }

    persistDraft() {
      const draft = { savedAt: Date.now(), counts: this.readCounts() };
      window.localStorage?.setItem(this.draftKey(), JSON.stringify(draft));
    }

    restoreDraft() {
      try {
        const draft = JSON.parse(window.localStorage?.getItem(this.draftKey()) || 'null');
        if (!isDraftFresh(draft)) return;
        const counts = draft.counts || {};
        const fields = {
          commonUncommon: counts.commonUncommonCount,
          holoReverse: counts.holoReverseCount ?? (positiveInteger(counts.holoCount) + positiveInteger(counts.reverseHoloCount)),
          doubleRare: counts.doubleRareCount,
        };
        Object.entries(fields).forEach(([name, value]) => {
          const input = this.root.querySelector(`[data-ankauf-bulk-input="${name}"]`);
          if (input) input.value = String(positiveInteger(value));
        });
      } catch (_error) {}
    }

    draftKey() {
      return `quitchibo:ankauf:${this.root.dataset.sectionId || 'main'}`;
    }
  }

  const findAnkaufRoots = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-ankauf-mode]')) roots.push(scope);
    roots.push(...Array.from(scope.querySelectorAll?.('[data-ankauf-mode]') || []));
    return roots;
  };

  const init = (scope = document) => {
    findAnkaufRoots(scope).forEach((root) => {
      if (!root.__ankaufMode) root.__ankaufMode = new AnkaufMode(root);
    });
  };

  const destroy = (scope = document) => {
    findAnkaufRoots(scope).forEach((root) => root.__ankaufMode?.destroy());
  };

  window.QuitchiboAnkaufTest = {
    AnkaufMode,
    buildShopifyCartUrl,
    canCreateCartLines,
    createCartPayload,
    destroy,
    init,
    isAcceptedUploadFile,
    isDraftFresh,
    money,
    getUploadFailureMessage,
    normalizeUploadResponse,
    parseRetryAfterSeconds,
    sanitizeCartProperties,
    shouldRejectProductionMock,
    termsVersion: TERMS_VERSION,
    requiredTerms: REQUIRED_TERMS,
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
}());
